import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/api';
import type {
  CreateUserInput,
  UpdateUserInput,
  UserImportMode,
  UsersListQuery,
} from '@/api/users';
import type { OrganizationRole } from '@/types';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ['users', 'stats'],
    queryFn: usersApi.stats,
  });
}

export function useUserSessions(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id, 'sessions'],
    queryFn: () => usersApi.listSessions(id!),
    enabled: !!id,
  });
}

export function useRevokeUserSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sessionId }: { id: string; sessionId?: string }) =>
      sessionId ? usersApi.revokeSession(id, sessionId) : usersApi.revokeAllSessions(id),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users', id, 'sessions'] });
    },
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: (id: string) => usersApi.resendVerification(id) });
}

export function useForceReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.forceReset(id),
    onSuccess: (_, id) => queryClient.invalidateQueries({ queryKey: ['users', id, 'sessions'] }),
  });
}

export function useUsersPage(query: UsersListQuery) {
  return useQuery({
    queryKey: ['users', 'page', query],
    queryFn: () => usersApi.listPage(query),
    placeholderData: (previous) => previous,
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserInput) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) => usersApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useImportUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, mode }: { csv: string; mode: UserImportMode }) =>
      usersApi.importCsv(csv, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUserProducts(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id, 'products'],
    queryFn: () => usersApi.listProducts(id!),
    enabled: !!id,
  });
}

export function useSetUserOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      role,
    }: {
      id: string;
      organizationId: string;
      role?: OrganizationRole;
    }) => usersApi.setOrganization(id, organizationId, role),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
  });
}

export function useRemoveUserOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.removeOrganization(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
  });
}
